import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { updateTrackerProgress } from '../../../../../redux/features/tutorReplacementTrackerSlice';
import { PROGRESS, PROGRESS_OPTIONS } from '../utils/trackerConstants';
import TrackerHistory from './TrackerHistory';

const Detail = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || '—'}
    </Typography>
  </Grid>
);

// The assignee (or the TA wing head) records Retained / Tutor change with remarks. The student's current CRO is
// shown with it, and the history below lists every earlier update.
export default function ProgressDialogContent({ row, onClose, onSaved }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(row.progressStatus === PROGRESS.PENDING ? '' : row.progressStatus);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const handleSubmit = async () => {
    if (!status || !remarks.trim()) {
      enqueueSnackbar('Select a status and add remarks', { variant: 'warning' });
      return;
    }
    setSaving(true);
    const action = await dispatch(
      updateTrackerProgress({ trainerAssignmentId: row.trainerAssignmentId, status, remarks: remarks.trim() }),
    );
    setSaving(false);
    const ok = action.payload?.status === 'success';
    enqueueSnackbar(action.payload?.message || (ok ? 'Progress updated' : 'Could not update progress'), {
      variant: ok ? 'success' : 'error',
    });
    if (ok) {
      setRemarks('');
      setHistoryKey((key) => key + 1);
      onSaved();
    }
  };

  return (
    <Stack gap={2} pt={1}>
      <Grid container spacing={1.5}>
        <Detail label="Student" value={`${row.studentName ?? '—'} (${row.admnNo ?? '—'})`} />
        <Detail label="Current CRO" value={row.croName} />
        <Detail label="Subject" value={row.subjectName} />
        <Detail label="Inactivated Tutor" value={row.oldTutorName} />
        <Detail label="Inactivation Reason" value={row.unmapReason} />
        <Detail label="New Tutor" value={row.newTutorAssigned ? row.newTutorName : 'Awaiting new tutor'} />
      </Grid>

      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {PROGRESS_OPTIONS.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        multiline
        minRows={3}
        required
        inputProps={{ maxLength: 500 }}
      />
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Please wait...' : 'Update Progress'}
        </Button>
      </Stack>

      <Divider />
      <Box>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          History
        </Typography>
        <TrackerHistory trainerAssignmentId={row.trainerAssignmentId} refreshKey={historyKey} />
      </Box>
    </Stack>
  );
}
